import { ActionButton, Condition, RuleResult, RuleType } from './types';
import { CourseProps, RegistrationStatus } from '../types';
import { ApplicationUser } from '../../hooks/auth/useAuth';
import { CourseCondition, FeatureFlagsCondition, getCourseCondition, getUserConditions, UserRequest } from './conditions';

export function applyRules(rules: RuleType[], conditions: Condition) {
    for (const rule of rules.sort((a, b) => b.priority - a.priority)) {
        const result = rule.resolve(conditions);
        if (!!result) {
            return result;
        }
    }
    return RuleResult.DENY;
}

const actionButtonBasedOnCourseCondition = ({
    isBeforeClassStartTime,
    isWaitlistEligible,
    hasSeatsAvailable,
    registrationStatus,
}: CourseCondition): ActionButton => {
    switch (registrationStatus) {
        case RegistrationStatus.REGISTERED:
            if (isBeforeClassStartTime) {
                return ActionButton.WITHDRAW;
            }
            break;
        case RegistrationStatus.WITHDRAWN:
        case RegistrationStatus.UNWAITLISTED:
        case RegistrationStatus.NOT_REGISTERED:
            if (hasSeatsAvailable) {
                return ActionButton.ENROLL;
            } else {
                if (isBeforeClassStartTime && isWaitlistEligible) {
                    return ActionButton.JOIN_WAITLIST;
                }
            }
            break;
        case RegistrationStatus.WAITLISTED:
            if (hasSeatsAvailable) {
                return ActionButton.ENROLL;
            } else {
                if (isBeforeClassStartTime) {
                    return ActionButton.UN_WAITLIST;
                }
            }
            break;
    }
    return ActionButton.NO_BUTTON;
};

const requestAccessButtonBasedOnCourseCondition = (
    courseCondition: CourseCondition,
    courseId: string,
    requestingAccessToOrgId: string | undefined,
    userRequests: UserRequest[] | undefined,
): ActionButton => {
    const { registrationStatus } = courseCondition;

    // Check if there are any existing pending or approved requests to other orgs
    if (requestingAccessToOrgId && userRequests) {
        const hasRequestToOtherOrg = userRequests.some(
            (request: UserRequest) =>
                request.orgId !== requestingAccessToOrgId && (request.requestStatus === 'PENDING' || request.requestStatus === 'APPROVED'),
        );

        if (hasRequestToOtherOrg) {
            return ActionButton.NO_BUTTON;
        }

        // Check if there's a matching request
        const matchingRequest = userRequests.find(
            (request: UserRequest) => request.resourceIds.includes(courseId) && request.orgId === requestingAccessToOrgId,
        );

        if (matchingRequest) {
            switch (matchingRequest.requestStatus) {
                case 'PENDING':
                    return ActionButton.REQUEST_ACCESS_PENDING;
                case 'APPROVED':
                    return ActionButton.REQUEST_ACCESS_APPROVED;
                case 'REJECTED':
                    return ActionButton.REQUEST_ACCESS_REJECTED;
                default:
                    return ActionButton.NO_BUTTON;
            }
        }
    }

    switch (registrationStatus) {
        case RegistrationStatus.NOT_REGISTERED:
            // TODO: Add hasSeatsAvailable counter when LADS stops redacting class_size
            // https://tasks.awstcprojects.com/browse/LEAP-2104
            return ActionButton.REQUEST_ACCESS;
    }
    return ActionButton.REQUEST_ACCESS_PENDING;
};

export function getActionButtonFromRules(
    rules: RuleType[],
    course: CourseProps,
    user: ApplicationUser,
    features: FeatureFlagsCondition,
    requestingAccessToOrgId?: string,
    userRequests?: any[],
): ActionButton {
    const condition: Condition = {
        courseCondition: getCourseCondition(course),
        userCondition: getUserConditions(user, requestingAccessToOrgId, userRequests),
        featureFlagsCondition: features,
    };
    const result = applyRules(rules, condition);
    switch (result) {
        case RuleResult.DENY:
            return ActionButton.NO_BUTTON;
        case RuleResult.ALLOW_REQUEST_ACCESS:
            if (condition.featureFlagsCondition.privateLinkSharing) {
                return requestAccessButtonBasedOnCourseCondition(
                    condition.courseCondition,
                    course.id,
                    condition.userCondition.requestingAccessToOrgId,
                    condition.userCondition.userRequests,
                );
            } else {
                return ActionButton.NO_BUTTON;
            }
        default:
            return actionButtonBasedOnCourseCondition(condition.courseCondition);
    }
}
